import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonButtons, IonContent, IonHeader, IonIcon, IonMenuButton, IonPage, IonRefresher, IonRefresherContent,
  IonSpinner, IonTitle, IonToolbar, RefresherEventDetail,
} from '@ionic/react';
import { alertCircleOutline, chatbubbleOutline, refreshOutline, ticketOutline, warningOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { dashboardService } from '../services/api';
import { DashboardOverview, DueDelivery, SalesWindow } from '../types';

// ── helpers ──────────────────────────────────────────────────────────────────
const gbp = (n: number | null | undefined) =>
  n == null ? '—' : '£' + Math.round(n).toLocaleString('en-GB');
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);
const dayLabel = (ymd: string, today: string) => {
  if (ymd === today) return 'Today';
  const d = new Date(`${ymd}T12:00:00Z`);
  const t = new Date(`${today}T12:00:00Z`);
  const diff = Math.round((d.getTime() - t.getTime()) / 864e5);
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return format(d, 'EEE d MMM');
};
const ago = (iso: string | null | undefined) => (iso ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true }) : 'never');
const staleMinutes = (iso: string | null | undefined) => (iso ? (Date.now() - new Date(iso).getTime()) / 60e3 : Infinity);
const shortPlatform = (p: string) =>
  ({ LiveFootballTickets: 'Live', HelloTickets: 'Hello', StubHub: 'StubHub', Tixstock: 'Tixstock', FTN: 'FTN', 'Vivid 1': 'Vivid 1', 'Vivid 2': 'Vivid 2' } as Record<string, string>)[p] || p;
const platformTone = (p: string) =>
  ({
    LiveFootballTickets: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    HelloTickets: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
    StubHub: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
    Tixstock: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    FTN: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  } as Record<string, string>)[p] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

const Delta: React.FC<{ now: number; prev: number; money?: boolean }> = ({ now, prev, money }) => {
  if (!prev) return <span className="text-xs text-gray-400">no prior period</span>;
  const d = ((now - prev) / prev) * 100;
  const up = d >= 0;
  return (
    <span className={'text-xs font-medium ' + (up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
      {up ? '▲' : '▼'} {Math.abs(Math.round(d))}% vs prev ({money ? gbp(prev) : prev.toLocaleString()})
    </span>
  );
};

const Card: React.FC<{ title?: React.ReactNode; right?: React.ReactNode; className?: string; children: React.ReactNode }> = ({ title, right, className = '', children }) => (
  <section className={'rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm ' + className}>
    {(title || right) && (
      <header className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string }> = ({ label, value, sub, tone = '' }) => (
  <div className={'rounded-xl px-3 py-2.5 bg-gray-50 dark:bg-gray-900/40 ' + tone}>
    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-xl font-bold text-gray-900 dark:text-gray-50 leading-tight">{value}</div>
    {sub && <div className="mt-0.5">{sub}</div>}
  </div>
);

const WindowCard: React.FC<{ title: string; w: SalesWindow; prev?: SalesWindow }> = ({ title, w, prev }) => (
  <Card title={title}>
    <div className="grid grid-cols-2 gap-2">
      <Stat label="Revenue" value={gbp(w.revenue)} sub={prev && <Delta now={w.revenue} prev={prev.revenue} money />} />
      <Stat label="Orders · tickets" value={`${w.orders} · ${w.tickets}`} sub={prev && <Delta now={w.orders} prev={prev.orders} />} />
      <Stat label="Est. profit" value={gbp(w.estProfit)} sub={<span className="text-xs text-gray-500">margin {pct(w.margin)} on {w.costedOrders} costed</span>} />
      <Stat label="Per ticket" value={w.tickets ? gbp(w.revenue / w.tickets) : '—'} sub={<span className="text-xs text-gray-500">avg revenue</span>} />
    </div>
  </Card>
);

// ── page ─────────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState<string | null>(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  // The due list runs to hundreds of rows before a big weekend; show the first ten so the rest of
  // the page stays reachable, with the full list a click away.
  const [showAllDue, setShowAllDue] = useState(false);
  const DUE_PREVIEW = 10;

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await dashboardService.getOverview();
      setData(d);
      // The server answers with its cached copy straight away and refreshes behind it; if that copy
      // was more than two minutes old, ask again shortly to pick up the fresh one.
      if (Date.now() - new Date(d.generatedAt).getTime() > 120_000) {
        setTimeout(async () => { try { setData(await dashboardService.getOverview()); } catch { /* keep what we have */ } }, 8000);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 120000); return () => clearInterval(id); }, [load]);
  const refresh = async (ev: CustomEvent<RefresherEventDetail>) => { await load(); ev.detail.complete(); };

  const due = useMemo(() => {
    const rows = data?.deliveries.orders || [];
    return rows.filter((o) =>
      (!platformFilter || o.platform === platformFilter) &&
      (!dayFilter || o.eventDay === dayFilter) &&
      (!onlyProblems || !o.phone || o.waFailed || !o.sourced),
    );
  }, [data, platformFilter, dayFilter, onlyProblems]);

  const dueShown = useMemo(() => (showAllDue ? due : due.slice(0, DUE_PREVIEW)), [due, showAllDue]);
  const dueByDay = useMemo(() => {
    const m = new Map<string, DueDelivery[]>();
    for (const o of dueShown) { if (!m.has(o.eventDay)) m.set(o.eventDay, []); m.get(o.eventDay)!.push(o); }
    return [...m.entries()];
  }, [dueShown]);

  const openChat = (phone: string) => { window.location.href = `/messages?chat=${phone}`; };
  const openDeliver = (phone: string) => { window.location.href = `/messages?deliver=${phone}`; };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton menu="main-menu" /></IonButtons>
          <IonTitle>{t('dashboard.title')}</IonTitle>
          <IonButtons slot="end">
            <button onClick={load} className="px-3 py-1.5 text-sm flex items-center gap-1 text-gray-600 dark:text-gray-300" title="Refresh">
              <IonIcon icon={refreshOutline} /> {data ? `updated ${ago(data.generatedAt)}` : ''}
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonRefresher slot="fixed" onIonRefresh={refresh}><IonRefresherContent /></IonRefresher>

        {loading && !data ? (
          <div className="flex items-center justify-center h-full text-gray-400 gap-2"><IonSpinner name="crescent" /> Loading…</div>
        ) : error && !data ? (
          <div className="p-6 text-rose-600">{error}</div>
        ) : data ? (
          <div className="p-4 md:p-6 space-y-5 max-w-[1500px] mx-auto">

            {/* ── Today strip ──────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Stat label="Deliveries due · 7 days" value={data.deliveries.total} sub={<span className="text-xs text-gray-500">{data.deliveries.tickets} tickets</span>} tone={data.deliveries.total ? 'ring-1 ring-amber-300 dark:ring-amber-700' : ''} />
              <Stat label="Due today · tomorrow" value={`${data.deliveries.todayCount} · ${data.deliveries.tomorrowCount}`} sub={<span className="text-xs text-gray-500">{data.deliveries.notStarted} not started</span>} tone={data.deliveries.todayCount ? 'ring-1 ring-rose-300 dark:ring-rose-700' : ''} />
              <Stat label="Held at the gate" value={data.whatsapp.held} sub={<span className="text-xs text-gray-500">{data.whatsapp.heldDueToday} auto-send within 24h</span>} />
              <Stat label="Needs attention" value={data.deliveries.noPhone + data.deliveries.waFailed} sub={<span className="text-xs text-gray-500">{data.deliveries.noPhone} no phone · {data.deliveries.waFailed} WhatsApp failed</span>} tone={data.deliveries.noPhone + data.deliveries.waFailed ? 'ring-1 ring-rose-300 dark:ring-rose-700' : ''} />
              <Stat label="Unsourced · 7 days" value={data.sourcing.within7d} sub={<span className="text-xs text-gray-500">{data.sourcing.orders} in 3 weeks · {gbp(data.sourcing.revenue)}</span>} tone={data.sourcing.within7d ? 'ring-1 ring-amber-300 dark:ring-amber-700' : ''} />
              <Stat label="Inbox" value={`${data.whatsapp.unread} unread`} sub={<span className="text-xs text-gray-500">{data.whatsapp.issues} issues · {data.whatsapp.awaitingOffer} offers open</span>} />
            </div>

            {/* ── Sales windows ────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <WindowCard title="Today" w={data.sales.windows.today} prev={data.sales.windows.yesterday} />
              <WindowCard title="Last 7 days" w={data.sales.windows.last7} prev={data.sales.windows.prev7} />
              <WindowCard title="Last 30 days" w={data.sales.windows.last30} prev={data.sales.windows.prev30} />
              <WindowCard title="Month to date" w={data.sales.windows.monthToDate} />
            </div>

            {/* ── Deliveries due ───────────────────────────────────────────── */}
            <Card
              title={<span className="flex items-center gap-2"><IonIcon icon={ticketOutline} /> Deliveries due in the next {data.deliveries.horizonDays} days — {data.deliveries.total} orders, {data.deliveries.tickets} tickets not sent yet</span>}
              right={
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> problems only
                </label>
              }
            >
              <div className="flex flex-wrap gap-1.5 mb-3">
                <button onClick={() => setPlatformFilter(null)} className={'px-2.5 py-1 rounded-full text-xs border ' + (!platformFilter ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}>All platforms</button>
                {Object.entries(data.deliveries.byPlatform).sort((a, b) => b[1] - a[1]).map(([p, n]) => (
                  <button key={p} onClick={() => setPlatformFilter(platformFilter === p ? null : p)} className={'px-2.5 py-1 rounded-full text-xs border ' + (platformFilter === p ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}>
                    {shortPlatform(p)} <span className="opacity-70">{n}</span>
                  </button>
                ))}
                <span className="mx-1 text-gray-300">|</span>
                {data.deliveries.byDay.filter((d) => d.orders).map((d) => (
                  <button key={d.day} onClick={() => setDayFilter(dayFilter === d.day ? null : d.day)} className={'px-2.5 py-1 rounded-full text-xs border ' + (dayFilter === d.day ? 'bg-amber-600 text-white border-amber-600' : 'border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200')}>
                    {dayLabel(d.day, data.today)} <span className="opacity-70">{d.orders}</span>
                  </button>
                ))}
              </div>

              {due.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">Nothing due — every order in the window is delivered.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr className="text-left">
                        <th className="py-1.5 pr-2">Game</th><th className="py-1.5 pr-2">Platform</th><th className="py-1.5 pr-2">Order</th><th className="py-1.5 pr-2">Buyer</th>
                        <th className="py-1.5 pr-2 text-right">Qty</th><th className="py-1.5 pr-2">Category</th><th className="py-1.5 pr-2">Bought from</th><th className="py-1.5 pr-2">Delivery</th><th className="py-1.5 pr-2">WhatsApp</th><th className="py-1.5 pr-2">Flags</th><th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {dueByDay.map(([day, rows]) => (
                        <React.Fragment key={day}>
                          <tr><td colSpan={11} className="pt-3 pb-1 text-xs font-semibold text-amber-700 dark:text-amber-300">{dayLabel(day, data.today)} · {format(new Date(`${day}T12:00:00Z`), 'd MMM')} — {due.filter((o) => o.eventDay === day).length} order{due.filter((o) => o.eventDay === day).length === 1 ? '' : 's'}, {due.filter((o) => o.eventDay === day).reduce((s, o) => s + o.quantity, 0)} tickets</td></tr>
                          {rows.map((o) => (
                            <tr key={o.orderId} className="border-t border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="py-1.5 pr-2 whitespace-nowrap max-w-[220px] truncate" title={o.eventName}>{o.eventName}</td>
                              <td className="py-1.5 pr-2"><span className={'px-1.5 py-0.5 rounded text-[11px] font-medium ' + platformTone(o.platform)}>{shortPlatform(o.platform)}</span></td>
                              <td className="py-1.5 pr-2 font-mono text-xs">{o.orderId}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap max-w-[160px] truncate" title={o.buyerName}>{o.buyerName || <span className="text-gray-400">—</span>}</td>
                              <td className="py-1.5 pr-2 text-right">{o.quantity}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap max-w-[180px] truncate" title={o.category}>{o.category}</td>
                              <td className={'py-1.5 pr-2 whitespace-nowrap max-w-[140px] truncate ' + (o.boughtFrom ? '' : 'text-amber-700 dark:text-amber-300')} title={o.boughtFrom || 'no supplier yet'}>{o.boughtFrom || 'not sourced'}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">
                                <span className={'px-1.5 py-0.5 rounded text-[11px] ' + (o.stage === 'awaiting_approval' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200')}>
                                  {o.stage === 'awaiting_approval' ? 'Waiting for buyer' : 'Not started'}
                                </span>
                                {o.held && o.forceAt && <span className="ml-1 text-[11px] text-gray-500">auto-send {format(new Date(o.forceAt), 'EEE HH:mm')}</span>}
                              </td>
                              <td className="py-1.5 pr-2 whitespace-nowrap text-xs">
                                <span className={o.waFailed ? 'text-rose-600 dark:text-rose-400 font-medium' : !o.phone ? 'text-rose-600 dark:text-rose-400' : 'text-gray-600 dark:text-gray-300'}>{o.waLabel}</span>
                                {o.withinWindow && <span className="ml-1 text-[10px] text-emerald-600">window open</span>}
                              </td>
                              <td className="py-1.5 pr-2 whitespace-nowrap text-[11px]">
                                {!o.sourced && <span className="mr-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">unsourced</span>}
                                {!o.phone && <span className="mr-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">no phone</span>}
                                {o.waFailed && <span className="mr-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">WhatsApp failed</span>}
                              </td>
                              <td className="py-1.5 whitespace-nowrap text-right">
                                {o.phone && (
                                  <>
                                    <button onClick={() => openChat(o.phone)} className="px-2 py-1 rounded-lg text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 mr-1" title="Open chat"><IonIcon icon={chatbubbleOutline} /></button>
                                    <button onClick={() => openDeliver(o.phone)} className="px-2 py-1 rounded-lg text-xs border border-teal-300 text-teal-700 dark:text-teal-300" title="Send tickets"><IonIcon icon={ticketOutline} /> Deliver</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {due.length > DUE_PREVIEW && (
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-500">Showing {dueShown.length} of {due.length}</span>
                      <button
                        onClick={() => setShowAllDue((v) => !v)}
                        className="px-3 py-1.5 rounded-lg text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {showAllDue ? `Show first ${DUE_PREVIEW}` : `Show all ${due.length}`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* ── Sales by platform + 14-day chart ─────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
              <Card title="Sales by platform — last 30 days" className="xl:col-span-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr className="text-left"><th className="py-1.5 pr-2">Platform</th><th className="py-1.5 pr-2 text-right">Orders</th><th className="py-1.5 pr-2 text-right">7d</th><th className="py-1.5 pr-2 text-right">Tickets</th><th className="py-1.5 pr-2 text-right">Revenue</th><th className="py-1.5 pr-2 text-right">£ / ticket</th><th className="py-1.5 pr-2 text-right">Margin</th><th className="py-1.5 text-right">Est. profit</th></tr>
                    </thead>
                    <tbody>
                      {data.sales.byPlatform.map((p) => (
                        <tr key={p.platform} className="border-t border-gray-100 dark:border-gray-700/60">
                          <td className="py-1.5 pr-2"><span className={'px-1.5 py-0.5 rounded text-[11px] font-medium ' + platformTone(p.platform)}>{shortPlatform(p.platform)}</span></td>
                          <td className="py-1.5 pr-2 text-right">{p.orders}</td>
                          <td className="py-1.5 pr-2 text-right text-gray-500">{p.last7}</td>
                          <td className="py-1.5 pr-2 text-right">{p.tickets}</td>
                          <td className="py-1.5 pr-2 text-right font-medium">{gbp(p.revenue)}</td>
                          <td className="py-1.5 pr-2 text-right">{p.tickets ? gbp(p.revenue / p.tickets) : '—'}</td>
                          <td className="py-1.5 pr-2 text-right text-gray-500" title={`${p.costedOrders} orders with a cost recorded`}>{pct(p.margin)}<span className="text-[10px] text-gray-400"> ({p.costedOrders})</span></td>
                          <td className="py-1.5 text-right">{gbp(p.estProfit)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold">
                        <td className="py-1.5 pr-2">Total</td>
                        <td className="py-1.5 pr-2 text-right">{data.sales.windows.last30.orders}</td>
                        <td className="py-1.5 pr-2 text-right text-gray-500">{data.sales.windows.last7.orders}</td>
                        <td className="py-1.5 pr-2 text-right">{data.sales.windows.last30.tickets}</td>
                        <td className="py-1.5 pr-2 text-right">{gbp(data.sales.windows.last30.revenue)}</td>
                        <td className="py-1.5 pr-2 text-right">{data.sales.windows.last30.tickets ? gbp(data.sales.windows.last30.revenue / data.sales.windows.last30.tickets) : '—'}</td>
                        <td className="py-1.5 pr-2 text-right">{pct(data.sales.windows.last30.margin)}</td>
                        <td className="py-1.5 text-right">{gbp(data.sales.windows.last30.estProfit)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-[11px] text-gray-400">Margin is measured on orders with a purchase cost recorded; est. profit applies that margin to all revenue.</p>
              </Card>
              <Card title="Revenue by day — last 14 days" className="xl:col-span-2">
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.sales.byDay14.map((d) => ({ ...d, label: format(new Date(`${d.day}T12:00:00Z`), 'EEE d') }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `£${Math.round(v / 1000)}k`} width={40} />
                      <Tooltip formatter={(v: any, name: any) => (name === 'revenue' ? gbp(Number(v)) : v)} labelFormatter={(l: any, p: any) => { const d = p?.[0]?.payload; return d ? `${l} — ${d.orders} orders, ${d.tickets} tickets` : String(l); }} />
                      <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>

            {/* ── Upcoming games + top events ──────────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
              <Card title="Upcoming games — next 14 days" className="xl:col-span-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr className="text-left"><th className="py-1.5 pr-2">Game</th><th className="py-1.5 pr-2">Date</th><th className="py-1.5 pr-2 text-right">Orders</th><th className="py-1.5 pr-2 text-right">Tickets</th><th className="py-1.5 pr-2 text-right">Revenue</th><th className="py-1.5 pr-2">Delivery</th><th className="py-1.5 pr-2 text-right">Unsourced</th><th className="py-1.5">Platforms</th></tr>
                    </thead>
                    <tbody>
                      {data.games.map((g) => (
                        <tr key={`${g.eventName}|${g.eventDay}`} className="border-t border-gray-100 dark:border-gray-700/60">
                          <td className="py-1.5 pr-2 whitespace-nowrap max-w-[240px] truncate" title={g.eventName}>{g.eventName}</td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">{dayLabel(g.eventDay, data.today)}</td>
                          <td className="py-1.5 pr-2 text-right">{g.orders}</td>
                          <td className="py-1.5 pr-2 text-right">{g.tickets}</td>
                          <td className="py-1.5 pr-2 text-right">{gbp(g.revenue)}</td>
                          <td className="py-1.5 pr-2 whitespace-nowrap">
                            <div className="flex h-2 w-32 rounded overflow-hidden bg-gray-200 dark:bg-gray-700" title={`${g.delivered} delivered · ${g.waiting} waiting for buyer · ${g.notStarted} not started`}>
                              <div className="bg-emerald-500" style={{ width: `${(g.delivered / g.orders) * 100}%` }} />
                              <div className="bg-amber-400" style={{ width: `${(g.waiting / g.orders) * 100}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-500">{g.delivered}/{g.orders} delivered{g.waiting ? ` · ${g.waiting} waiting` : ''}{g.notStarted ? ` · ${g.notStarted} not started` : ''}</span>
                          </td>
                          <td className={'py-1.5 pr-2 text-right ' + (g.unsourced ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-400')}>{g.unsourced || '—'}</td>
                          <td className="py-1.5 whitespace-nowrap text-[11px] text-gray-500">{Object.entries(g.platforms).map(([p, n]) => `${shortPlatform(p)} ${n}`).join(' · ')}</td>
                        </tr>
                      ))}
                      {data.games.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-gray-400">No games with orders in the next 14 days.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Card title="Top games by revenue — last 30 days" className="xl:col-span-2">
                <table className="w-full text-sm">
                  <tbody>
                    {data.sales.topEvents.map((e) => (
                      <tr key={e.eventName} className="border-t border-gray-100 dark:border-gray-700/60 first:border-0">
                        <td className="py-1.5 pr-2 max-w-[220px] truncate" title={e.eventName}>{e.eventName}</td>
                        <td className="py-1.5 pr-2 text-right text-gray-500 whitespace-nowrap">{e.orders} · {e.tickets}t</td>
                        <td className="py-1.5 text-right font-medium whitespace-nowrap">{gbp(e.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>

            {/* ── Health ───────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card title="Order importers" right={<span className="text-[11px] text-gray-400">last order seen on each platform</span>}>
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400"><tr className="text-left"><th className="py-1 pr-2">Platform</th><th className="py-1 pr-2">Last order</th><th className="py-1 pr-2">Imported</th><th className="py-1 text-right">Orders · 7d</th></tr></thead>
                  <tbody>
                    {data.health.imports.map((r) => {
                      const stale = r.orders7d > 0 && staleMinutes(r.lastImportedAt) > 24 * 60;
                      return (
                        <tr key={r.platform} className="border-t border-gray-100 dark:border-gray-700/60">
                          <td className="py-1 pr-2"><span className={'px-1.5 py-0.5 rounded text-[11px] font-medium ' + platformTone(r.platform)}>{shortPlatform(r.platform)}</span></td>
                          <td className="py-1 pr-2 whitespace-nowrap">{ago(r.lastOrderAt)}</td>
                          <td className={'py-1 pr-2 whitespace-nowrap ' + (stale ? 'text-rose-600 dark:text-rose-400' : '')}>{stale && <IonIcon icon={warningOutline} className="align-middle mr-1" />}{ago(r.lastImportedAt)}</td>
                          <td className="py-1 text-right">{r.orders7d}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
              <Card title="Repricing bots" right={<span className="text-[11px] text-gray-400">last snapshot written per platform</span>}>
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400"><tr className="text-left"><th className="py-1 pr-2">Platform</th><th className="py-1 pr-2">Last run</th><th className="py-1 pr-2 text-right">Events</th><th className="py-1 text-right">Listings</th></tr></thead>
                  <tbody>
                    {data.health.bots.map((b) => {
                      const stale = staleMinutes(b.lastRunAt) > 60;
                      return (
                        <tr key={b.platform} className="border-t border-gray-100 dark:border-gray-700/60">
                          <td className="py-1 pr-2 capitalize">{b.platform}</td>
                          <td className={'py-1 pr-2 whitespace-nowrap ' + (stale ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-300')}>{stale && <IonIcon icon={alertCircleOutline} className="align-middle mr-1" />}{ago(b.lastRunAt)}</td>
                          <td className="py-1 pr-2 text-right">{b.events}</td>
                          <td className="py-1 text-right">{b.listings.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-2 text-[11px] text-gray-400">Red = no snapshot in the last hour (FTN's bot is known to be off). Inbox: {data.whatsapp.failedThreads7d} buyer{data.whatsapp.failedThreads7d === 1 ? '' : 's'} where every message this week failed.</p>
              </Card>
            </div>
          </div>
        ) : null}
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;
